/**
 * =============================================================================
 * PASSPORT CUSTOMIZER - Main Controller
 * =============================================================================
 *
 * This IIFE handles all passport customizer functionality including:
 * - State management for passport data
 * - Modal open/close operations
 * - Form validation and progress tracking
 * - Image cropping with Cropper.js
 * - Preview generation
 * - localStorage persistence
 *
 * =============================================================================
 */
(function () {
  // ========== CONFIGURATION & STATE ==========

  // Get passport count and product tags from modal element data attributes
  const modal = document.getElementById('passport-customizer-modal');
  if (!modal) {
    console.error('Passport customizer modal not found');
    return;
  }

  const PASSPORT_COUNT = parseInt(modal.getAttribute('data-passport-count')) || 1;
  if (PASSPORT_COUNT === 0) {
    console.error('Invalid passport count');
    return;
  }

  // Translated UI strings from Liquid data attributes
  const TEXT_UPLOAD_PHOTO = modal.getAttribute('data-text-upload-photo') || 'Subir Foto';
  const TEXT_CHANGE_PHOTO = modal.getAttribute('data-text-change-photo') || 'Cambiar Foto';
  const TEXT_ALL_COMPLETED = modal.getAttribute('data-text-all-completed') || '✓ Todos los datos completados';
  const TEXT_CLICK_TO_COMPLETE = modal.getAttribute('data-text-click-to-complete') || 'Haz clic para completar los datos';

  // Get product tags and filter edition tags (e.g., 'europe-edition', 'usa-edition')
  const productTagsString = modal.getAttribute('data-product-tags') || '';
  const productTags = productTagsString ? productTagsString.split(',').map(tag => tag.trim()) : [];
  const editionTags = productTags.filter(tag => tag.includes('edition'));
  const isCompact = productTags.includes('compact');
  const isCaminoDeSantiago = productTags.includes('Camino de Santiago');
  const isSnow = productTags.includes('snow');

  /**
   * Centralized function to determine edition key from tags
   * Follows Single Responsibility: only responsible for edition detection
   * @param {string} editionTag - Edition tag from card (may be empty for snow/camino)
   * @returns {string} - Edition key ('europe', 'usa', 'world', 'snow', 'camino')
   */
  function getEditionKey(editionTag = '') {
    // Combine edition tag and product tags for comprehensive checking
    const allTags = (editionTag + ' ' + productTags.join(' ')).toLowerCase();

    // Check in order of specificity
    if (allTags.includes('europe')) return 'europe';
    if (allTags.includes('usa')) return 'usa';
    if (allTags.includes('snow')) return 'snow';
    if (allTags.includes('camino') || allTags.includes('santiago')) return 'camino';

    // Default to world edition
    return 'world';
  }

  const passportData = Array(PASSPORT_COUNT)
    .fill(null)
    .map(() => ({
      firstName: '',
      lastName: '',
      fecha: '', // Only for Camino de Santiago and Snow
      nationality: '',
      modalidad: '', // Only for Snow passport
      phrase: '',
      photo: null,
      color: 'Aleatorio', // UV flashlight color (default for non-compact)
    }));

  let globalColor = 'Aleatorio'; // Global color for compact products
  let expandedPassportIndex = 0;
  let previousCompletedCount = 0;
  let updateTimeout = null;
  let isSubmitting = false;
  let processingImages = 0; // Track number of images being processed

  // ========== INITIALIZATION ==========

  function initializeCustomizer() {
    // Open first card
    const firstCard = document.querySelector('.passport-card[data-passport-index="0"]');
    if (firstCard) {
      togglePassportCard(0);
    }

    // Update progress state (completion status, checkmarks, etc.)
    updateProgress();
  }

  // ========== MODAL CONTROLS ==========

  window.openPassportCustomizer = function () {
    const modal = document.getElementById('passport-customizer-modal');
    if (!modal) return;

    // Scroll to top before opening modal
    window.scrollTo(0, 0);

    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      modal.classList.add('passport-customizer-modal--active');
    });

    modal.addEventListener('transitionend', function onTransitionEnd(e) {
      if (e.target === modal && e.propertyName === 'opacity') {
        modal.removeEventListener('transitionend', onTransitionEnd);
        initializeCustomizer();
      }
    });
  };

  window.closePassportCustomizer = async function () {
    const modal = document.getElementById('passport-customizer-modal');
    if (!modal) return;

    // Save passport data to localStorage (without alert)
    try {
      const dataToSave = await Promise.all(
        passportData.map(async (passport) => {
          const passportCopy = { ...passport };
          // Convert blob URL to base64 if present
          if (passportCopy.photo && passportCopy.photo.startsWith('blob:')) {
            passportCopy.photo = await blobUrlToBase64(passportCopy.photo);
          }
          return passportCopy;
        })
      );

      localStorage.setItem('passportData', JSON.stringify(dataToSave));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('Cannot save: storage limit exceeded');
      } else {
        console.error('Error saving passport data:', e);
      }
    }

    modal.classList.remove('passport-customizer-modal--active');

    modal.addEventListener('transitionend', function cleanup(e) {
      if (e.target === modal && e.propertyName === 'opacity') {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        modal.removeEventListener('transitionend', cleanup);

        // Reset the customization flag so the confirmation modal shows again
        if (window.resetCustomizationFlag) {
          window.resetCustomizationFlag();
        }
      }
    });
  };

  // ========== CARD EXPANSION ==========

  window.togglePassportCard = function (index) {
    const card = document.querySelector(`.passport-card[data-passport-index="${index}"]`);
    if (!card) return;

    const content = card.querySelector('.passport-card__content');
    const chevron = card.querySelector('.passport-card__chevron');
    const isExpanded = content?.classList.contains('passport-card__content--expanded');

    // Close all cards
    document.querySelectorAll('.passport-card__content').forEach((c) => {
      c.classList.remove('passport-card__content--expanded');
    });
    document.querySelectorAll('.passport-card__chevron').forEach((ch) => {
      ch.classList.remove('passport-card__chevron--expanded');
    });
    document.querySelectorAll('.passport-card').forEach((c) => {
      c.classList.remove('passport-card--expanded');
    });

    // Open this card if it wasn't expanded
    if (!isExpanded) {
      content?.classList.add('passport-card__content--expanded');
      chevron?.classList.add('passport-card__chevron--expanded');
      card.classList.add('passport-card--expanded');
      expandedPassportIndex = index;

      // Scroll the passport form content to top
      if (content) {
        content.scrollTop = 0;
      }

      // Wait for expansion animation to complete (400ms), then scroll to card header
      setTimeout(() => {
        const header = card.querySelector('.passport-card__header');
        if (header) {
          header.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 450);
    }
  };

  // ========== DATA MANAGEMENT ==========

  function updatePassportData(index, field, value) {
    passportData[index][field] = value;

    // Debounce progress updates to avoid lag on every keystroke
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    updateTimeout = setTimeout(() => {
      updateProgress();
    }, 200);
  }

  // Handle input changes with event delegation
  document.addEventListener('input', function (e) {
    // Exclude file inputs - they are handled separately by handlePhotoUpload
    if (e.target.type === 'file') {
      return;
    }

    if (e.target.dataset.field && e.target.dataset.index !== undefined) {
      const index = parseInt(e.target.dataset.index);
      const field = e.target.dataset.field;
      updatePassportData(index, field, e.target.value);

      // Update character counter for phrase textarea
      if (field === 'phrase') {
        const counter = document.getElementById(`passport-phrase-counter-${index}`);
        if (counter) {
          counter.textContent = e.target.value.length;
        }
      }
    }
  });

  // Handle radio button selection (for color selection)
  function handleRadioSelection(target) {
    if (target.type === 'radio' && target.dataset.field === 'color') {
      console.log('[COLOR DEBUG] Target element:', target);
      console.log('[COLOR DEBUG] Target parent:', target.parentElement);
      console.log('[COLOR DEBUG] Looking for closest .passport-card...');

      // ALWAYS find the passport card to get the correct index
      const passportCard = target.closest('.passport-card');

      console.log('[COLOR DEBUG] Found passport card:', passportCard);
      console.log('[COLOR DEBUG] Card data-passport-index:', passportCard ? passportCard.getAttribute('data-passport-index') : 'NONE');

      if (!passportCard) {
        console.error('[COLOR DEBUG] ERROR: No passport card found for radio button!');
        return;
      }

      const cardIndex = passportCard.getAttribute('data-passport-index');
      const index = parseInt(cardIndex);

      console.log('[COLOR DEBUG] Final index:', index);
      console.log('[COLOR DEBUG] Updating passport data:', index, 'color', target.value);

      // Update the passport data
      updatePassportData(index, 'color', target.value);

      // Uncheck ALL other color radios within THIS passport card only
      const allColorRadios = passportCard.querySelectorAll(`input[type="radio"][data-field="color"]`);
      allColorRadios.forEach(radio => {
        radio.checked = (radio === target);
      });

      console.log('[COLOR DEBUG] Updated passport', index, 'color to:', target.value);
      console.log('[COLOR DEBUG] PassportData[' + index + '].color:', passportData[index].color);
    }
  }

  // Track last processed radio to prevent duplicate handling
  let lastProcessedRadio = { id: null, timestamp: 0 };

  // Handle change events (for radio buttons and select dropdowns)
  document.addEventListener('change', function (e) {
    // Handle select dropdowns (e.g., modalidad)
    if (e.target.tagName === 'SELECT' && e.target.dataset.field && e.target.dataset.index !== undefined) {
      const index = parseInt(e.target.dataset.index);
      const field = e.target.dataset.field;
      updatePassportData(index, field, e.target.value);
      return;
    }

    // Handle radio buttons (color selection)
    if (e.target.type === 'radio' && e.target.dataset.field === 'color') {
      // Prevent duplicate processing within 50ms
      const now = Date.now();
      if (lastProcessedRadio.id === e.target.id && now - lastProcessedRadio.timestamp < 50) {
        console.log('[COLOR DEBUG] Skipping duplicate change event');
        return;
      }
      lastProcessedRadio = { id: e.target.id, timestamp: now };

      console.log('[COLOR DEBUG] Change event fired:', {
        type: e.target.type,
        id: e.target.id,
        value: e.target.value,
        checked: e.target.checked,
        hasField: !!e.target.dataset.field,
        hasIndex: e.target.dataset.index !== undefined,
        index: e.target.dataset.index,
        field: e.target.dataset.field
      });

      handleRadioSelection(e.target);
    }
  });

  // Handle global color change (for compact products)
  window.handleGlobalColorChange = function (color) {
    globalColor = color;

    // Update badge icon based on color selection
    const badge = document.getElementById('global-color-badge');
    if (badge) {
      const circle = badge.querySelector('.passport-card__badge-circle');
      const checkmark = badge.querySelector('.passport-card__badge-check');

      if (color === 'Aleatorio') {
        // Show circle for "Aleatorio" (random)
        badge.classList.remove('passport-card__badge--complete');
        badge.classList.add('passport-card__badge--incomplete');
        if (circle) circle.classList.remove('pc-hidden');
        if (checkmark) checkmark.classList.add('pc-hidden');
      } else {
        // Show checkmark for specific color selection
        badge.classList.remove('passport-card__badge--incomplete');
        badge.classList.add('passport-card__badge--complete');
        if (circle) circle.classList.add('pc-hidden');
        if (checkmark) checkmark.classList.remove('pc-hidden');
      }
    }
  };

  // ========== IMAGE CROPPING (Cropper.js) ==========

  const croppers = Array(PASSPORT_COUNT).fill(null).map(() => ({ current: null }));

  // Show cropper modal
  function showCropper(index) {
    const modal = document.getElementById(`passport-cropper-modal-${index}`);
    if (modal) {
      modal.style.display = 'flex';
      modal.offsetHeight; // Force reflow
      modal.classList.add('active');
    }
  }

  // Close cropper modal
  function closeCropper(index, shouldClearImage = false) {
    const modal = document.getElementById(`passport-cropper-modal-${index}`);
    const cropperObj = croppers[index];

    if (modal) {
      modal.classList.remove('active');

      if (shouldClearImage) {
        // Clear preview
        const preview = document.querySelector(`[data-photo-preview="${index}"]`);
        const img = document.querySelector(`[data-photo-img="${index}"]`);
        if (preview && img) {
          preview.classList.add('pc-hidden');
          img.src = '';
        }

        // Clear input
        const input = document.querySelector(`[data-photo-input="${index}"]`);
        if (input) input.value = '';

        // Clear passport data
        passportData[index].photo = null;

        // Reset button text and style
        const buttonText = document.querySelector(`[data-photo-button-text="${index}"]`);
        if (buttonText) {
          buttonText.textContent = TEXT_UPLOAD_PHOTO;
          const button = buttonText.closest('.passport-form__photo-button');
          if (button) button.classList.remove('passport-form__photo-button--secondary');
        }

        // Update progress to reflect the change
        updateProgress();
      }

      setTimeout(() => {
        modal.style.display = 'none';
        if (cropperObj && cropperObj.current) {
          cropperObj.current.destroy();
          cropperObj.current = null;
        }
      }, 300);
    }
  }

  // Prepare and initialize cropper
  function prepareCropper(index, file) {
    const maxSize = 15 * 1024 * 1024; // 15MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    // Validate file size
    if (file.size > maxSize) {
      alert('Image is too large. Maximum file size is 15MB.');
      const input = document.querySelector(`[data-photo-input="${index}"]`);
      if (input) input.value = '';
      // Don't clear existing photo data or UI - user just selected wrong file
      return;
    }

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid image format. Please upload a JPG or PNG image.');
      const input = document.querySelector(`[data-photo-input="${index}"]`);
      if (input) input.value = '';
      // Don't clear existing photo data or UI - user just selected wrong file
      return;
    }

    const reader = new FileReader();
    reader.onload = function(e) {
      const cropperImage = document.getElementById(`passport-cropper-image-${index}`);
      const cropperObj = croppers[index];

      if (cropperImage) {
        cropperImage.src = e.target.result;
        showCropper(index);

        // Destroy existing cropper if any
        if (cropperObj && cropperObj.current) {
          cropperObj.current.destroy();
        }

        // Initialize new cropper with passport photo aspect ratio (3:4)
        cropperObj.current = new Cropper(cropperImage, {
          aspectRatio: 3 / 4,
          viewMode: 1,
          dragMode: 'move',
          autoCropArea: 1,
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false,
        });
      }
    };
    reader.readAsDataURL(file);
  }

  // Crop and save image
  function cropAndSaveImage(index) {
    const cropperObj = croppers[index];
    if (!cropperObj || !cropperObj.current) return;

    // Show processing state
    processingImages++;
    const cropBtn = document.getElementById(`passport-crop-button-${index}`);
    if (cropBtn) {
      cropBtn.disabled = true;
      cropBtn.textContent = 'Processing...';
    }

    const canvas = cropperObj.current.getCroppedCanvas({
      width: 375,
      height: 500
    });

    canvas.toBlob(function(blob) {
      const croppedFile = new File([blob], 'passport-photo.jpg', { type: 'image/jpeg' });

      // Update file input
      const input = document.querySelector(`[data-photo-input="${index}"]`);
      if (input) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);
        input.files = dataTransfer.files;
      }

      // Update preview
      const preview = document.querySelector(`[data-photo-preview="${index}"]`);
      const img = document.querySelector(`[data-photo-img="${index}"]`);
      const uploadArea = document.querySelector(`[data-photo-upload-area="${index}"]`);

      // Revoke old blob URL to prevent memory leak
      if (passportData[index].photo && passportData[index].photo.startsWith('blob:')) {
        URL.revokeObjectURL(passportData[index].photo);
      }

      const blobUrl = URL.createObjectURL(blob);

      if (preview && img) {
        preview.classList.remove('pc-hidden');
        img.src = blobUrl;
      }

      // Hide upload area, show preview container
      if (uploadArea) uploadArea.classList.add('pc-hidden');

      // Store photo in passport data
      passportData[index].photo = blobUrl;

      // Clear processing state
      processingImages--;
      if (cropBtn) {
        cropBtn.disabled = false;
        cropBtn.textContent = 'Crop & Save';
      }

      // Close cropper and update progress
      closeCropper(index, false);
      updateProgress();
    }, 'image/jpeg', 0.9);
  }

  // Handle photo upload with cropper
  window.handlePhotoUpload = function (index, input) {
    const file = input.files?.[0];
    if (file) {
      prepareCropper(index, file);
    }
  };

  // Remove photo
  window.removePhoto = function (index) {
    // Clear the passport data
    passportData[index].photo = null;

    // Clear the file input
    const input = document.getElementById(`passport-photo-${index}`);
    if (input) {
      input.value = '';
    }

    // Hide the preview
    const preview = document.querySelector(`[data-photo-preview="${index}"]`);
    const previewImg = document.querySelector(`[data-photo-img="${index}"]`);
    if (preview) {
      preview.classList.add('pc-hidden');
    }
    if (previewImg) {
      previewImg.src = '';
    }

    // Show upload area again
    const uploadArea = document.querySelector(`[data-photo-upload-area="${index}"]`);
    if (uploadArea) uploadArea.classList.remove('pc-hidden');

    // Update progress
    updateProgress();
  };

  // Setup cropper event listeners
  for (let i = 0; i < PASSPORT_COUNT; i++) {
    // Close buttons
    const closeBtn = document.querySelector(`.passport-cropper-close[data-cropper-index="${i}"]`);
    const cancelBtn = document.getElementById(`passport-cancel-crop-${i}`);
    const cropBtn = document.getElementById(`passport-crop-button-${i}`);

    if (closeBtn) {
      closeBtn.addEventListener('click', () => closeCropper(i, true));
    }
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => closeCropper(i, true));
    }
    if (cropBtn) {
      cropBtn.addEventListener('click', () => cropAndSaveImage(i));
    }
  }

  // ========== PROGRESS TRACKING & VALIDATION ==========

  function isPassportComplete(passport) {
    // For non-compact products, color is required per passport
    // For compact products, global color is always set, so we don't check per-passport color
    const colorValid = isCompact || passport.color;

    // Base required fields
    const baseFieldsValid = passport.firstName && passport.lastName && passport.nationality && passport.photo;

    // For Camino de Santiago: fecha is required, phrase is required
    if (isCaminoDeSantiago) {
      return baseFieldsValid && passport.fecha && passport.phrase && colorValid;
    }

    // For Snow: fecha (temporada) is required, modalidad is required, phrase is NOT required
    if (isSnow) {
      return baseFieldsValid && passport.fecha && passport.modalidad && colorValid;
    }

    // For regular passports: phrase is required
    return baseFieldsValid && passport.phrase && colorValid;
  }

  function updateProgress() {
    let completedCount = 0;

    // Calculate completion and update each card
    passportData.forEach((passport, index) => {
      const isComplete = isPassportComplete(passport);
      if (isComplete) completedCount++;

      const card = document.querySelector(`.passport-card[data-passport-index="${index}"]`);
      if (!card) return;

      const badge = card.querySelector('[data-badge]');
      const badgeNumber = badge?.querySelector('.passport-card__badge-number');
      const badgeCheck = badge?.querySelector('.passport-card__badge-check');
      const subtitle = card.querySelector('[data-subtitle]');
      const completion = card.querySelector('[data-completion]');

      if (isComplete) {
        card.classList.add('passport-card--complete');
        badge?.classList.remove('passport-card__badge--incomplete');
        badge?.classList.add('passport-card__badge--complete');
        badgeNumber?.classList.add('pc-hidden');
        badgeCheck?.classList.remove('pc-hidden');

        if (subtitle) subtitle.textContent = TEXT_ALL_COMPLETED;
        completion?.classList.add('passport-completion--show');
      } else {
        card.classList.remove('passport-card--complete');
        badge?.classList.remove('passport-card__badge--complete');
        badge?.classList.add('passport-card__badge--incomplete');
        badgeNumber?.classList.remove('pc-hidden');
        badgeCheck?.classList.add('pc-hidden');

        if (subtitle) subtitle.textContent = TEXT_CLICK_TO_COMPLETE;
        completion?.classList.remove('passport-completion--show');
      }
    });

    // Update progress bar
    const progressBar = document.getElementById('passport-progress-bar');
    if (progressBar) {
      const percentage = (completedCount / PASSPORT_COUNT) * 100;
      progressBar.style.width = `${percentage}%`;
    }

    // Update count
    const countEl = document.getElementById('passport-completed-count');
    if (countEl && completedCount !== previousCompletedCount) {
      countEl.classList.add('updating');
      countEl.textContent = `${completedCount}/${PASSPORT_COUNT}`;
      setTimeout(() => {
        countEl.classList.remove('updating');
      }, 300);
      previousCompletedCount = completedCount;
    }

    // Update checkout button
    const checkoutBtn = document.getElementById('passport-checkout-button');
    const checkoutBtnText = document.getElementById('passport-checkout-button-text');
    if (checkoutBtn && checkoutBtnText) {
      const isProcessing = processingImages > 0;

      // Only disable if images are still processing
      checkoutBtn.disabled = isProcessing;

      if (isProcessing) {
        checkoutBtnText.textContent = 'Procesando imágenes...';
      } else {
        checkoutBtnText.textContent = 'Añadir al carrito';
      }
    }
  }

  // Go to next passport
  window.goToNextPassport = function (currentIndex) {
    const nextIndex = currentIndex + 1;
    if (nextIndex < PASSPORT_COUNT) {
      togglePassportCard(nextIndex);
    }
  };

  // Open preview modal
  window.openPassportPreview = function (index) {
    const passport = passportData[index];
    const modal = document.getElementById('passport-preview-modal');
    const previewData = document.getElementById('passport-preview-data');

    // Show preview data overlay (it may be hidden from example modal)
    if (previewData) previewData.style.display = 'block';

    // Update title
    const title = document.getElementById('passport-preview-title');
    if (title) {
      const fullName = `${passport.firstName} ${passport.lastName}`.trim();
      title.textContent = `Vista Previa - ${fullName || `Pasaporte ${index + 1}`}`;
    }

    // Get edition tag for this passport (from the card's edition tag)
    const passportCard = document.querySelector(`.passport-card[data-passport-index="${index}"]`);
    const editionSpan = passportCard?.querySelector('.passport-title-edition');
    const editionTag = editionSpan?.getAttribute('data-edition-tag') || '';

    // Determine which template to use (using centralized function)
    const editionKey = getEditionKey(editionTag);

    // Load template URLs from JSON
    const templateUrlsElement = document.getElementById('passport-template-urls');
    const templateUrls = templateUrlsElement ? JSON.parse(templateUrlsElement.textContent) : {};
    const templateUrl = templateUrls[editionKey];

    // Set template image
    const templateImg = document.getElementById('passport-preview-template');
    if (templateImg && templateUrl) {
      templateImg.src = templateUrl;
    }

    // Update overlaid data
    const previewName = document.getElementById('preview-name');
    const previewSurname = document.getElementById('preview-surname');
    const previewNationality = document.getElementById('preview-nationality');
    const previewPhrase = document.getElementById('preview-phrase');
    const previewFecha = document.getElementById('preview-fecha');
    const previewPhoto = document.getElementById('preview-photo');

    if (previewName) previewName.textContent = passport.firstName || '';
    if (previewSurname) previewSurname.textContent = passport.lastName || '';
    if (previewNationality) previewNationality.textContent = passport.nationality || '';
    if (previewPhrase) previewPhrase.textContent = passport.phrase || '';

    // Only show fecha if there's data (for Camino de Santiago and Snow products)
    if (previewFecha) {
      if (passport.fecha) {
        previewFecha.textContent = passport.fecha;
        previewFecha.style.display = 'block';
      } else {
        previewFecha.textContent = '';
        previewFecha.style.display = 'none';
      }
    }

    if (previewPhoto && passport.photo) {
      previewPhoto.src = passport.photo;
      previewPhoto.style.display = 'block';
    } else if (previewPhoto) {
      previewPhoto.style.display = 'none';
    }

    // Show modal
    modal.classList.add('passport-preview-modal--open');
    document.body.style.overflow = 'hidden';
  };

  // Close preview modal
  window.closePassportPreview = function () {
    const modal = document.getElementById('passport-preview-modal');
    modal.classList.remove('passport-preview-modal--open');
    document.body.style.overflow = '';
  };

  // Download passport preview as image using html2canvas
  window.downloadPassportPreview = function () {
    const wrapper = document.getElementById('passport-preview-wrapper');
    const saveBtn = document.getElementById('passport-preview-save-btn');

    if (!wrapper) return;

    // Show loading state immediately
    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Generando...';
    saveBtn.disabled = true;
    saveBtn.blur(); // Remove focus to reset hover state

    // Double rAF ensures browser has time to repaint before heavy work
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        html2canvas(wrapper, {
          useCORS: true,
          allowTaint: false,
          scale: 2,
          backgroundColor: null
        }).then(canvas => {
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'mi-pasaporte.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            saveBtn.textContent = originalText;
            saveBtn.disabled = false;
          }, 'image/png');
        }).catch(() => {
          alert('Error al generar la imagen. Por favor, inténtalo de nuevo.');
          saveBtn.textContent = originalText;
          saveBtn.disabled = false;
        });
      });
    });
  };

  // ========== CHECKOUT & PERSISTENCE ==========

  // Get cart URL preserving the current language prefix from the URL path
  function getWeglotCartUrl() {
    const firstPart = window.location.pathname.split('/')[1];
    return (firstPart && firstPart.length === 2) ? `/${firstPart}/cart` : '/cart';
  }
  window.getWeglotCartUrl = getWeglotCartUrl;

  // Convert blob URL to base64 data URL for localStorage persistence
  async function blobUrlToBase64(blobUrl) {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting blob to base64:', error);
      return null;
    }
  }

  window.saveForLater = async function () {
    // Convert blob URLs to base64 before saving
    try {
      const dataToSave = await Promise.all(
        passportData.map(async (passport) => {
          const passportCopy = { ...passport };
          // Convert blob URL to base64 if present
          if (passportCopy.photo && passportCopy.photo.startsWith('blob:')) {
            passportCopy.photo = await blobUrlToBase64(passportCopy.photo);
          }
          return passportCopy;
        })
      );

      localStorage.setItem('passportData', JSON.stringify(dataToSave));
      alert('Passport data saved! You can continue later.');
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('Cannot save: storage limit exceeded');
        alert('Unable to save all data. Storage limit exceeded. Some photos may not persist.');
      } else {
        console.error('Error saving passport data:', e);
        alert('Error saving passport data. Please try again.');
      }
    }
  };

  // Continue to checkout
  window.continueToCheckout = function () {
    // Prevent double-submit or submission while images are processing
    if (isSubmitting) return;
    if (processingImages > 0) {
      alert('Please wait for images to finish processing.');
      return;
    }
    isSubmitting = true;

    // Update button state
    const checkoutBtn = document.getElementById('passport-checkout-button');
    const checkoutBtnText = document.getElementById('passport-checkout-button-text');
    if (checkoutBtn) checkoutBtn.disabled = true;
    if (checkoutBtnText) checkoutBtnText.textContent = 'Processing...';

    // Get the product form ID from the modal
    const modal = document.getElementById('passport-customizer-modal');
    const productFormId = modal?.getAttribute('data-product-form-id');

    if (!productFormId) {
      alert('Error: Product form not found. Please refresh and try again.');
      isSubmitting = false;
      if (checkoutBtn) checkoutBtn.disabled = false;
      if (checkoutBtnText) checkoutBtnText.textContent = 'Continue to Checkout';
      return;
    }

    // Get the variant ID from the main product form
    const variantIdInput = document.querySelector(`#${productFormId} input[name="id"]`);
    const variantId = variantIdInput ? variantIdInput.value : null;

    if (!variantId) {
      alert('Error: Product variant not found. Please refresh and try again.');
      isSubmitting = false;
      if (checkoutBtn) checkoutBtn.disabled = false;
      if (checkoutBtnText) checkoutBtnText.textContent = 'Continue to Checkout';
      return;
    }

    // Create FormData for cart submission
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    // Include cart-drawer sections in the add request so we can re-render from the single response
    const cartDrawerForSections = document.querySelector('cart-drawer');
    if (cartDrawerForSections && typeof cartDrawerForSections.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartDrawerForSections.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    }

    // Function to convert blob URL to File object
    async function blobUrlToFile(blobUrl, fileName) {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new File([blob], fileName, { type: 'image/jpeg' });
    }

    // Add properties for each passport
    async function addPassportProperties() {
      for (let i = 0; i < PASSPORT_COUNT; i++) {
        const passport = passportData[i];
        const passportNumber = i + 1;

        // Add passport edition (matches legacy form logic)
        // Uses edition tag from product tags, with fallback to first edition or empty string
        formData.append(
          `properties[Pasaporte${passportNumber}]`,
          editionTags[i] || editionTags[0] || ''
        );

        // Add color property (matches legacy form logic - Color comes second)
        // Always use numbered suffix (Color1, Color2, etc.)
        if (isCompact) {
          // Compact products: single global color for all passports (only add once on first passport)
          if (i === 0) {
            formData.append(`properties[Color${passportNumber}]`, globalColor || 'Aleatorio');
          }
        } else {
          // Non-compact: individual color per passport with numbered suffix
          formData.append(`properties[Color${passportNumber}]`, passport.color || 'Aleatorio');
        }

        // Add passport properties with numbered suffix (in legacy order)
        // Send whatever data exists, even if empty
        formData.append(`properties[Nombre${passportNumber}]`, passport.firstName || '');
        formData.append(`properties[Apellidos${passportNumber}]`, passport.lastName || '');
        // Fecha only for Camino de Santiago and Snow products
        if ((isCaminoDeSantiago || isSnow) && passport.fecha) {
          formData.append(`properties[Fecha${passportNumber}]`, passport.fecha);
        }
        formData.append(`properties[Nacionalidad${passportNumber}]`, passport.nationality || '');
        // Modalidad only for Snow products
        if (isSnow && passport.modalidad) {
          formData.append(`properties[Modalidad${passportNumber}]`, passport.modalidad);
        }
        formData.append(`properties[Frase${passportNumber}]`, passport.phrase || '');

        // Handle photo upload - convert blob URL to File
        if (passport.photo) {
          try {
            const photoFile = await blobUrlToFile(passport.photo, `passport-${passportNumber}.jpg`);
            formData.append(`properties[Imagen${passportNumber}]`, photoFile);
          } catch (error) {
            console.error(`Error converting photo for passport ${passportNumber}:`, error);
          }
        }
      }
    }

    // Add all passport properties and then submit to cart
    addPassportProperties().then(() => {
      // Send to Shopify cart API
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(async (data) => {
        // Save passport data to localStorage (convert blob URLs to base64)
        try {
          const dataToSave = await Promise.all(
            passportData.map(async (passport) => {
              const passportCopy = { ...passport };
              // Convert blob URL to base64 if present
              if (passportCopy.photo && passportCopy.photo.startsWith('blob:')) {
                passportCopy.photo = await blobUrlToBase64(passportCopy.photo);
              }
              return passportCopy;
            })
          );
          localStorage.setItem('passportData', JSON.stringify(dataToSave));
        } catch (e) {
          if (e.name === 'QuotaExceededError') {
            console.warn('Cannot save: storage limit exceeded');
          } else {
            console.error('Error saving passport data:', e);
          }
        }

        // Close customizer and open cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          window.closePassportCustomizer();
          cartDrawer.renderContents(data);
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          isSubmitting = false;
          if (checkoutBtn) checkoutBtn.disabled = false;
          if (checkoutBtnText) checkoutBtnText.textContent = 'Añadir al carrito';
        } else {
          window.location.href = getWeglotCartUrl();
        }
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        alert('Error al añadir al carrito. Por favor, inténtalo de nuevo.');
        isSubmitting = false;
        if (checkoutBtn) checkoutBtn.disabled = false;
        if (checkoutBtnText) checkoutBtnText.textContent = 'Añadir al carrito';
      });
    }).catch(error => {
      console.error('Error preparing passport data:', error);
      alert('Error al preparar los datos del pasaporte. Por favor, inténtalo de nuevo.');
      isSubmitting = false;
      if (checkoutBtn) checkoutBtn.disabled = false;
      if (checkoutBtnText) checkoutBtnText.textContent = 'Añadir al carrito';
    });
  };

  // Load saved data on init
  const savedData = localStorage.getItem('passportData');
  if (savedData) {
    try {
      const parsed = JSON.parse(savedData);
      parsed.forEach((data, index) => {
        if (index < PASSPORT_COUNT) {
          Object.assign(passportData[index], data);

          // Restore form values
          if (data.firstName) {
            const firstNameInput = document.getElementById(`passport-name-${index}`);
            if (firstNameInput) firstNameInput.value = data.firstName;
          }
          if (data.lastName) {
            const lastNameInput = document.getElementById(`passport-surname-${index}`);
            if (lastNameInput) lastNameInput.value = data.lastName;
          }
          if (data.nationality) {
            const nationalityInput = document.getElementById(`passport-nationality-${index}`);
            if (nationalityInput) nationalityInput.value = data.nationality;
          }
          if (data.fecha) {
            const fechaInput = document.getElementById(`passport-fecha-${index}`);
            if (fechaInput) fechaInput.value = data.fecha;
          }
          if (data.modalidad) {
            const modalidadInput = document.getElementById(`passport-modalidad-${index}`);
            if (modalidadInput) modalidadInput.value = data.modalidad;
          }
          if (data.phrase) {
            const phraseInput = document.getElementById(`passport-phrase-${index}`);
            if (phraseInput) {
              phraseInput.value = data.phrase;
              // Update character counter
              const counter = document.getElementById(`passport-phrase-counter-${index}`);
              if (counter) {
                counter.textContent = data.phrase.length;
              }
            }
          }
          if (data.color && !isCompact) {
            // Restore color selection for non-compact products
            // Use data-field and data-index attributes instead of name (more reliable across different HTML structures)
            const colorRadios = document.querySelectorAll(`input[type="radio"][data-field="color"][data-index="${index}"]`);
            colorRadios.forEach(radio => {
              if (radio.value === data.color) {
                radio.checked = true;
              } else {
                radio.checked = false;
              }
            });
          }
          if (data.photo) {
            // Hide upload area and show preview container
            const uploadArea = document.querySelector(`[data-photo-upload-area="${index}"]`);
            if (uploadArea) uploadArea.classList.add('pc-hidden');

            const preview = document.querySelector(`[data-photo-preview="${index}"]`);
            const img = document.querySelector(`[data-photo-img="${index}"]`);
            if (preview && img) {
              preview.classList.remove('pc-hidden');
              img.src = data.photo;
            }
          }
        }
      });
      updateProgress();
    } catch (e) {
      console.error('Error loading saved passport data:', e);
    }
  }

  // Copy passport data from one passport to another
  window.copyPassportData = function (fromIndexStr, toIndex) {
    const fromIndex = parseInt(fromIndexStr);

    // If empty selection, do nothing
    if (fromIndexStr === '' || isNaN(fromIndex)) {
      return;
    }

    // Copy all fields including photo
    passportData[toIndex].firstName = passportData[fromIndex].firstName;
    passportData[toIndex].lastName = passportData[fromIndex].lastName;
    passportData[toIndex].nationality = passportData[fromIndex].nationality;
    passportData[toIndex].fecha = passportData[fromIndex].fecha;
    passportData[toIndex].modalidad = passportData[fromIndex].modalidad;
    passportData[toIndex].phrase = passportData[fromIndex].phrase;
    passportData[toIndex].color = passportData[fromIndex].color;
    passportData[toIndex].photo = passportData[fromIndex].photo;

    // Update UI for all copied fields
    const firstNameInput = document.getElementById(`passport-name-${toIndex}`);
    if (firstNameInput) firstNameInput.value = passportData[toIndex].firstName;

    const lastNameInput = document.getElementById(`passport-surname-${toIndex}`);
    if (lastNameInput) lastNameInput.value = passportData[toIndex].lastName;

    const nationalityInput = document.getElementById(`passport-nationality-${toIndex}`);
    if (nationalityInput) nationalityInput.value = passportData[toIndex].nationality;

    const fechaInput = document.getElementById(`passport-fecha-${toIndex}`);
    if (fechaInput) fechaInput.value = passportData[toIndex].fecha;

    const modalidadInput = document.getElementById(`passport-modalidad-${toIndex}`);
    if (modalidadInput) modalidadInput.value = passportData[toIndex].modalidad;

    const phraseInput = document.getElementById(`passport-phrase-${toIndex}`);
    if (phraseInput) {
      phraseInput.value = passportData[toIndex].phrase;
      // Update character counter
      const counter = document.getElementById(`passport-phrase-counter-${toIndex}`);
      if (counter) {
        counter.textContent = passportData[toIndex].phrase.length;
      }
    }

    // Copy color selection (if not compact)
    if (!isCompact && passportData[toIndex].color) {
      const colorRadios = document.querySelectorAll(`input[type="radio"][data-field="color"][data-index="${toIndex}"]`);
      colorRadios.forEach(radio => {
        radio.checked = (radio.value === passportData[toIndex].color);
      });
    }

    // Copy photo
    if (passportData[toIndex].photo) {
      const buttonText = document.querySelector(`[data-photo-button-text="${toIndex}"]`);
      if (buttonText) {
        buttonText.textContent = TEXT_CHANGE_PHOTO;
        const button = buttonText.closest('.passport-form__photo-button');
        if (button) button.classList.add('passport-form__photo-button--secondary');
      }

      const preview = document.querySelector(`[data-photo-preview="${toIndex}"]`);
      const img = document.querySelector(`[data-photo-img="${toIndex}"]`);
      if (preview && img) {
        preview.classList.remove('pc-hidden');
        img.src = passportData[toIndex].photo;
      }
    }

    // Update progress
    updateProgress();
  };

  // Open example modal for passport - reuses the preview modal
  window.openPassportExampleModal = function (index) {
    const modal = document.getElementById('passport-preview-modal');
    const templateImg = document.getElementById('passport-preview-template');
    const previewData = document.getElementById('passport-preview-data');
    const title = document.getElementById('passport-preview-title');

    if (!modal || !templateImg || !previewData) return;

    // Get edition tag for this passport
    const passportCard = document.querySelector(`.passport-card[data-passport-index="${index}"]`);
    const editionSpan = passportCard?.querySelector('.passport-title-edition');
    const editionTag = editionSpan?.getAttribute('data-edition-tag') || '';

    // Determine which template to use (using centralized function)
    const editionKey = getEditionKey(editionTag);

    // Load example URLs from JSON (use example URLs, not preview templates)
    const exampleUrlsElement = document.getElementById('passport-example-urls');
    const exampleUrls = exampleUrlsElement ? JSON.parse(exampleUrlsElement.textContent) : {};
    const exampleUrl = exampleUrls[editionKey] || exampleUrls['world'];

    // Update modal title
    if (title) title.textContent = 'Ejemplo del Pasaporte';

    // Set example image
    templateImg.src = exampleUrl;

    // Hide all preview data overlay (no form data shown for example)
    if (previewData) previewData.style.display = 'none';

    // Show modal
    modal.classList.add('passport-preview-modal--open');
    document.body.style.overflow = 'hidden';
  };

  // ========== EXPOSED FOR INCOMPLETE PASSPORTS CHECK ==========
  // Expose necessary functions for incomplete passports modal
  window.getPassportData = function() {
    return passportData;
  };

  window.isPassportComplete = isPassportComplete;

  window.openPassportCard = function(index) {
    window.togglePassportCard(index);
  };

  // Expose configuration for add to cart functionality
  window.getPassportConfig = function() {
    return {
      PASSPORT_COUNT,
      editionTags,
      isCompact,
      globalColor
    };
  };

  // Expose helper function for blob to base64 conversion
  window.blobUrlToBase64 = blobUrlToBase64;
})();

// ========== CUSTOMIZATION CONFIRMATION MODAL ==========
(function() {
  'use strict';

  let pendingFormSubmit = null;
  let hasCustomized = false;

  // Track if user has customized (opened the customizer)
  const originalOpenCustomizer = window.openPassportCustomizer;
  window.openPassportCustomizer = function() {
    hasCustomized = true;
    if (originalOpenCustomizer) {
      originalOpenCustomizer();
    }
  };

  // Intercept product form submission
  document.addEventListener('DOMContentLoaded', function() {
    const productForms = document.querySelectorAll('product-form form');
    
    productForms.forEach(form => {
      form.addEventListener('submit', function(e) {
        // Check if this is a passport product (has the customizer modal)
        const customizerModal = document.getElementById('passport-customizer-modal');
        if (!customizerModal) return; // Not a passport product, allow normal submission

        // If user hasn't customized, show confirmation modal
        if (!hasCustomized) {
          e.preventDefault();
          e.stopPropagation();
          pendingFormSubmit = { form, event: e };
          showCustomizationConfirmModal();
        }
        // Otherwise, allow normal submission
      }, true); // Use capture phase to intercept before product-form.js
    });
  });

  // Show confirmation modal
  function showCustomizationConfirmModal() {
    const modal = document.getElementById('customization-confirm-modal');
    if (!modal) return;

    modal.style.display = 'flex';
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    // Close on overlay click
    const overlay = modal.querySelector('.customization-confirm-modal__overlay');
    if (overlay) {
      overlay.onclick = closeCustomizationConfirmModal;
    }
  }

  // Close confirmation modal
  window.closeCustomizationConfirmModal = function() {
    const modal = document.getElementById('customization-confirm-modal');
    if (!modal) return;

    modal.classList.remove('active');
    setTimeout(() => {
      modal.style.display = 'none';
      pendingFormSubmit = null;
    }, 300);
  };

  // Add to cart without customization (with empty passport properties)
  window.addToCartWithoutCustomization = async function() {
    // Hide confirmation modal
    const confirmModal = document.getElementById('customization-confirm-modal');
    if (confirmModal) {
      confirmModal.classList.remove('active');
      confirmModal.style.display = 'none';
    }

    // Get variant ID from the pending form
    const variantIdInput = pendingFormSubmit?.form?.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;

    // Clear pending state
    pendingFormSubmit = null;

    if (!variantId) {
      alert('Error al añadir al carrito. Por favor, inténtalo de nuevo.');
      return;
    }

    // Get passport config
    const config = window.getPassportConfig ? window.getPassportConfig() : null;
    const passportCount = config?.PASSPORT_COUNT || 1;
    const editionTags = config?.editionTags || [];
    const isCompact = config?.isCompact || false;
    const globalColor = config?.globalColor || 'Aleatorio';

    // Build form data with empty passport properties
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    // Add properties for each passport (empty values - cart template handles display)
    for (let i = 0; i < passportCount; i++) {
      const num = i + 1;

      formData.append(`properties[Pasaporte${num}]`, editionTags[i] || editionTags[0] || '');

      if (isCompact) {
        if (i === 0) formData.append(`properties[Color${num}]`, globalColor);
      } else {
        formData.append(`properties[Color${num}]`, 'Aleatorio');
      }

      formData.append(`properties[Nombre${num}]`, '');
      formData.append(`properties[Apellidos${num}]`, '');
      formData.append(`properties[Nacionalidad${num}]`, '');
      formData.append(`properties[Frase${num}]`, '');
    }

    // Include cart-drawer sections in the add request so we can re-render from the single response
    const cartDrawerForSections = document.querySelector('cart-drawer');
    if (cartDrawerForSections && typeof cartDrawerForSections.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartDrawerForSections.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();

      // Close confirmation modal and open cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
        cartDrawer.renderContents(data);
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
      } else {
        window.location.href = window.getWeglotCartUrl();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error al añadir al carrito. Por favor, inténtalo de nuevo.');
    }
  };

  // Reset customization flag (called when user closes customizer without adding to cart)
  window.resetCustomizationFlag = function() {
    hasCustomized = false;
  };

  // Open customizer from confirmation modal
  window.openCustomizerFromConfirm = function() {
    closeCustomizationConfirmModal();

    if (window.openPassportCustomizer) {
      window.openPassportCustomizer();
    }
  };

})();

// ========== INCOMPLETE PASSPORTS MODAL ==========
(function() {
  'use strict';

  let incompletePassportNumbers = [];
  let skipIncompleteCheck = false;

  // Modify the continueToCheckout function to check for incomplete passports
  const originalContinueToCheckout = window.continueToCheckout;

  window.continueToCheckout = function() {
    // Skip check if user already chose to continue without completing
    if (skipIncompleteCheck) {
      skipIncompleteCheck = false;
      return originalContinueToCheckout();
    }

    // Check if we're in the customizer modal (has passport data accessor)
    if (typeof window.getPassportData !== 'function') {
      return originalContinueToCheckout();
    }

    // Identify incomplete passports
    incompletePassportNumbers = identifyIncompletePassports();

    // If there are incomplete passports, show modal
    if (incompletePassportNumbers.length > 0) {
      showIncompletePassportsModal();
      return;
    }

    // All passports complete, proceed to checkout
    originalContinueToCheckout();
  };

  // Identify which passports are completely empty (no fields filled except color/lantern)
  function identifyIncompletePassports() {
    const incomplete = [];
    const passportData = window.getPassportData();

    if (!passportData) return incomplete;

    for (let i = 0; i < passportData.length; i++) {
      const passport = passportData[i];
      const passportNumber = i + 1;

      // Check if passport is completely empty (no firstName, lastName, nationality, phrase, or photo)
      // Color/lantern selection doesn't count as having data
      const hasAnyData = passport.firstName || passport.lastName || passport.nationality || passport.phrase || passport.photo;

      if (!hasAnyData) {
        incomplete.push(passportNumber);
      }
    }

    return incomplete;
  }

  // Show the incomplete passports modal
  function showIncompletePassportsModal() {
    const modal = document.getElementById('incomplete-passports-modal');
    const contentContainer = document.getElementById('incomplete-passports-content');
    const actionsContainer = document.getElementById('incomplete-passports-actions');

    if (!modal || !contentContainer || !actionsContainer) return;

    // Get the appropriate template based on count
    const templateId = incompletePassportNumbers.length === 1
      ? 'incomplete-passports-template-single'
      : 'incomplete-passports-template-multiple';

    const template = document.getElementById(templateId);
    if (!template) return;

    // Clone template content
    const templateContent = template.innerHTML;

    // Clear existing content
    contentContainer.innerHTML = '';
    actionsContainer.innerHTML = '';

    // Create temporary container to parse template
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = templateContent;

    // Extract paragraph for content
    const paragraph = tempDiv.querySelector('p');

    if (paragraph) {
      paragraph.innerHTML = paragraph.innerHTML.replace('{number}', incompletePassportNumbers.join(', '));
      contentContainer.appendChild(paragraph);
    }

    // Extract buttons for actions
    const buttons = tempDiv.querySelectorAll('button');
    buttons.forEach(button => {
      actionsContainer.appendChild(button);
    });

    // Show modal with animation
    modal.style.display = 'flex';
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    // Close on overlay click
    const overlay = modal.querySelector('.customization-confirm-modal__overlay');
    if (overlay) {
      overlay.onclick = window.closeIncompletePassportsModal;
    }
  }

  // Close incomplete passports modal
  window.closeIncompletePassportsModal = function() {
    const modal = document.getElementById('incomplete-passports-modal');
    if (!modal) return;

    modal.classList.remove('active');
    setTimeout(() => {
      modal.style.display = 'none';
    }, 300);
  };

  // Continue without completing the incomplete passports
  window.continueWithoutCompletingPassports = function() {
    // Immediately hide modal without animation to prevent flash
    const modal = document.getElementById('incomplete-passports-modal');
    if (modal) {
      modal.classList.remove('active');
      modal.style.display = 'none';
    }

    skipIncompleteCheck = true;
    window.continueToCheckout();
  };

  // Personalize the first incomplete passport
  window.personalizeIncompletePassport = function() {
    // Immediately hide modal without animation to prevent flash
    const modal = document.getElementById('incomplete-passports-modal');
    if (modal) {
      modal.classList.remove('active');
      modal.style.display = 'none';
    }

    if (incompletePassportNumbers.length > 0) {
      const firstIncompleteIndex = incompletePassportNumbers[0] - 1;
      const card = document.querySelector(`.passport-card[data-passport-index="${firstIncompleteIndex}"]`);

      if (card) {
        const content = card.querySelector('.passport-card__content');
        const isAlreadyOpen = content?.classList.contains('passport-card__content--expanded');

        // Only toggle if the card is not already open
        if (!isAlreadyOpen && window.openPassportCard) {
          window.openPassportCard(firstIncompleteIndex);
        }

        // Scroll to the card
        setTimeout(() => {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  };

})();

// ========== EMOJI PREVENTION ==========
(function() {
  'use strict';

  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}]/gu;

  function initEmojiPrevention() {
    const inputs = document.querySelectorAll('.no-emoji-input');
    const errorText = 'No se permiten emojis';

    inputs.forEach((input) => {
      // Create error message element
      const errorMsg = document.createElement('span');
      errorMsg.className = 'emoji-error-message';
      errorMsg.textContent = errorText;
      errorMsg.style.cssText = 'color: #d32f2f; font-size: 1.2rem; display: none; margin-top: 4px;';

      // Insert after the input
      input.parentNode.insertBefore(errorMsg, input.nextSibling);

      let hideTimeout;

      input.addEventListener('input', function(e) {
        const hadEmoji = emojiRegex.test(this.value);

        // Remove emojis
        const newValue = this.value.replace(emojiRegex, '');
        if (newValue !== this.value) {
          this.value = newValue;
        }

        // Show/hide error message
        if (hadEmoji) {
          this.style.borderColor = '#d32f2f';
          errorMsg.style.display = 'block';

          // Clear existing timeout
          clearTimeout(hideTimeout);

          // Hide after 3 seconds
          hideTimeout = setTimeout(() => {
            errorMsg.style.display = 'none';
            this.style.borderColor = '';
          }, 3000);
        } else {
          // User typed normal text, hide error immediately
          errorMsg.style.display = 'none';
          this.style.borderColor = '';
          clearTimeout(hideTimeout);
        }
      });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEmojiPrevention);
  } else {
    initEmojiPrevention();
  }

})();